import { Post } from '../types/post';

export interface ExportOptions {
  quality: 'high' | 'medium' | 'low';
  format: 'mp4' | 'webm';
}

export class CompilationService {
  private readonly STORAGE_KEYS = {
    FAVORITES: 'favorites',
    COMPILATION_POSTS: 'compilationPosts'
  };

  constructor() {}

  // Favorites management
  loadFavorites(): Post[] {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEYS.FAVORITES);
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      console.error('Error loading favorites:', e);
      return [];
    }
  }

  saveFavorites(favorites: Post[]) {
    try {
      localStorage.setItem(this.STORAGE_KEYS.FAVORITES, JSON.stringify(favorites));
    } catch (e) {
      console.error('Error saving favorites:', e);
    }
  }

  toggleFavorite(post: Post, currentFavorites: Post[]): Post[] {
    const favorites = [...currentFavorites];
    const index = favorites.findIndex(p => p.data.id === post.data.id);
    
    if (index === -1) {
      favorites.push(post);
    } else {
      favorites.splice(index, 1);
    }
    
    this.saveFavorites(favorites);
    return favorites;
  }

  isFavorite(post: Post, favorites: Post[]): boolean {
    return favorites.some(p => p.data.id === post.data.id);
  }

  // Compilation management
  loadCompilationPosts(): Post[] {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEYS.COMPILATION_POSTS);
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      console.error('Error loading compilation posts:', e);
      return [];
    }
  }

  saveCompilationPosts(posts: Post[]) {
    try {
      localStorage.setItem(this.STORAGE_KEYS.COMPILATION_POSTS, JSON.stringify(posts));
    } catch (e) {
      console.error('Error saving compilation posts:', e);
    }
  }

  addToCompilation(post: Post, currentPosts: Post[]): Post[] {
    if (!currentPosts.some(p => p.data.id === post.data.id)) {
      const updatedPosts = [...currentPosts, post];
      this.saveCompilationPosts(updatedPosts);
      return updatedPosts;
    }
    return currentPosts;
  }

  removeFromCompilation(post: Post, currentPosts: Post[]): Post[] {
    const posts = [...currentPosts];
    const index = posts.findIndex(p => p.data.id === post.data.id);
    
    if (index !== -1) {
      posts.splice(index, 1);
      this.saveCompilationPosts(posts);
    }
    
    return posts;
  }

  // Video compilation
  async compileVideos(posts: Post[], options: ExportOptions = {
    quality: 'high',
    format: 'mp4'
  }): Promise<Blob> {
    // This is a placeholder for video compilation logic
    // In a real implementation, you would:
    // 1. Download all videos
    // 2. Use WebAssembly-based video processing library or backend service
    // 3. Combine videos and return the result
    await new Promise(resolve => setTimeout(resolve, 2000));
    return new Blob(['placeholder'], { type: 'video/mp4' });
  }
}
